import { RccCode, RccCodes, getRequestBaseUrl, getTenantConfig, TenantSources, getAmbientRoutingRcc } from "gl-commonui";

export type TenantId = number | null;

export interface TenantScoped {
    tenantId?: TenantId;
}

export type TenantValidationStatus = "idle" | "valid" | "mixed" | "unresolved" | "mismatch" | "unsupportedRegion";

export interface TenantValidationResult {
    status: TenantValidationStatus;
    tenantId?: TenantId;
    tenantIds?: TenantId[];
    writeTenantId?: TenantId;
}

// Confirmed tenant ids for this feature: null = Global, 1 = US, 2 = China, 3 = Russia.
// `1: "US"` is local to this guard, distinct from gl-commonui's own RCC-based tenant enum.
const TENANT_NAMES: { [key: string]: string } = {
    "null": "Global",
    "1": "US",
    "2": "China",
    "3": "Russia",
};

// Normalizes a RAW tenantId to this guard's own canonical contract (null = Global). Most
// sources genuinely return `null` for Global (e.g. `/admin/v1/notifications/targetcount`),
// but some backend data — notably gl-commonui's GetRegionInfo response
// (`RegionInfoModel.tenantId: number`, consumed via accessibleRegions/regionInfo for
// Individual User targets) — uses the raw numeric sentinel `0` for Global instead. Without
// this, a `0`-tagged target is bucketed as its own distinct/unmapped tenant: wrongly
// `mixed` against a genuine `null`-tagged Global target, and unresolvable by
// tenantIdToWriteRcc()/isTenantGatewayConfigured() (neither maps "0"), which fails the write
// closed even though the target is really just Global. `undefined` (unresolved) passes
// through unchanged.
export function normalizeTenantId(tenantId: TenantId | undefined): TenantId | undefined {
    if (tenantId === undefined) {
        return undefined;
    }
    return tenantId === 0 ? null : tenantId;
}

export function tenantDisplayName(tenantId: TenantId): string {
    const key = tenantId === null || tenantId === undefined ? "null" : String(tenantId);
    return TENANT_NAMES[key] || `Tenant ${tenantId}`;
}

// Maps a resolved tenant to the __gl_region__ override value used to force a write to its
// gateway. Deliberately covers only the 4 known tenants — anything else fails closed
// (undefined) rather than defaulting to Global.
const TENANT_ID_TO_WRITE_RCC: { [key: string]: RccCode } = {
    "null": RccCodes.ForceGlobal, // gl-commonui's sentinel to force Global from any TLD
    "1": RccCodes.US,
    "2": RccCodes.China,
    "3": RccCodes.Russia,
};

export function tenantIdToWriteRcc(tenantId: TenantId | undefined): RccCode | undefined {
    if (tenantId === undefined) {
        return undefined;
    }
    const key = tenantId === null ? "null" : String(tenantId);
    return TENANT_ID_TO_WRITE_RCC[key];
}

// Resolves the tenant a list of targets share: `unresolved` if any tenantId is missing,
// `mixed` if they disagree, `valid` with the shared tenantId otherwise.
export function analyzeTargetTenants(items: TenantScoped[]): TenantValidationResult {
    if (!items || items.length === 0) {
        return { status: "idle" };
    }
    const normalized = items.map((item) => normalizeTenantId(item.tenantId));
    if (normalized.some((tenantId) => tenantId === undefined)) {
        return { status: "unresolved" };
    }
    const distinct = Array.from(new Set(normalized as TenantId[]));
    if (distinct.length > 1) {
        return { status: "mixed", tenantIds: distinct };
    }
    return { status: "valid", tenantId: distinct[0] };
}

// True only when gl-commonui resolved the current tenant context from the real hostname
// (never a route-path or JWT-token fallback) — this is what makes `.ru`/`.us` isolation
// trustworthy against a stale or forged token.
function isGenuinelyOnSite(rcc: RccCode): boolean {
    const config = getTenantConfig();
    return !!config && config.source === TenantSources.Tld && config.rccCode === rcc;
}

export function isCurrentSiteRussia(): boolean {
    return isGenuinelyOnSite(RccCodes.Russia);
}

export function isCurrentSiteUS(): boolean {
    return isGenuinelyOnSite(RccCodes.US);
}

// The US School TEST/PROD deployments run at a `.com` hostname (e.g.
// ustests-schools.grapeseed.com), not `.us` — TLD detection alone can't tell them apart
// from a genuine Global deployment. `deploymentRcc` is a build-time identity marker
// injected via webpack DefinePlugin (`deploymentRcc: JSON.stringify("us")` in
// webpack.ustest.config.js/webpack.usprod.config.js) — which build this bundle was
// produced for, never the current user's tenant, a selected region, URL routing config, or
// a JWT/token claim. Generic, not Notification-specific; this guard is just its first user.
function isUSDeploymentBuild(): boolean {
    return typeof process !== "undefined" && !!process.env && process.env.deploymentRcc === "us";
}

// True for a genuine `.us` hostname OR a build whose own deploymentRcc marks it as the US
// deployment. This is what the notification guard's US-only restriction actually keys off
// — isCurrentSiteUS() alone would wrongly treat US School TEST/PROD as Global.
export function isUSNotificationDeployment(): boolean {
    return isCurrentSiteUS() || isUSDeploymentBuild();
}

// Default owner tenant for a site with no explicit region context — a UI preference for
// picking a default among already-resolved options, never used for routing/validation.
export function currentSiteDefaultOwnerTenantId(): TenantId {
    if (isCurrentSiteRussia()) {
        return 3;
    }
    if (isCurrentSiteUS()) {
        return 1;
    }
    return null;
}

const RCC_TO_TENANT_ID: { [key: string]: TenantId } = {
    [RccCodes.Russia]: 3,
    [RccCodes.US]: 1,
    [RccCodes.China]: 2,
    [RccCodes.Global]: null,
};

// The tenant an AMBIENT (no `__gl_region__` override) notification request actually
// reaches — delegates to gl-commonui's own `getAmbientRoutingRcc()`, the single source of
// truth `buildRequestUrl()` itself uses (including its live-token China fallback), so this
// can never drift from real routing behavior. Deliberately different from
// `isCurrentSiteRussia()`/`isCurrentSiteUS()`, which stay TLD-only for the `.ru`-isolation
// boundary; use this only to validate an already-ambient request, never to decide whether a
// request should be ambient in the first place.
export function effectiveAmbientTenantId(): TenantId {
    const rcc = getAmbientRoutingRcc();
    return rcc in RCC_TO_TENANT_ID ? RCC_TO_TENANT_ID[rcc] : null;
}

// `.ru` only reaches Russia. The US notification deployment (genuine `.us`, or a `.com`
// build carrying `deploymentRcc: "us"` — see isUSNotificationDeployment()) only reaches US:
// it shares the same admin/account data as Global (so Role/Region/School/Campus/Class/User
// pickers can surface Global/US/China options there), but the notification DB is per-tenant
// and its apiPrefixV2 gateway is dedicated to the US tenant only (it also serves
// nexus/student there and must not be repointed) — there is no Global/China gateway
// reachable from it. Every other site reaches everything except Russia.
export function isTenantReachableFromCurrentSite(tenantId: TenantId): boolean {
    if (isCurrentSiteRussia()) {
        return tenantId === 3;
    }
    if (isUSNotificationDeployment()) {
        return tenantId === 1;
    }
    return tenantId !== 3;
}

// CREATE: a target resolving to a single, site-reachable tenant is always valid — the
// write force-routes to that tenant's own gateway (resolveWriteRcc()), independent of any
// list filter. Fails closed as `unsupportedRegion` for a tenant the current site can't
// reach (Russia off `.ru`, or any non-Russia tenant genuinely on `.ru`).
export function analyzeCreateTarget(items: TenantScoped[]): TenantValidationResult {
    const analysis = analyzeTargetTenants(items);
    if (analysis.status === "valid" && !isTenantReachableFromCurrentSite(analysis.tenantId)) {
        return { status: "unsupportedRegion", tenantId: analysis.tenantId };
    }
    return analysis;
}

// EDIT: the backend has no move/upsert-between-tenants operation, so a target must match
// the tenant the notification already lives in (`notificationTenantId`) — anything else
// blocks as `mismatch` rather than force-routing elsewhere. An undefined owner fails closed
// as `unresolved`. A target the current site can't even reach at all (e.g. a Global/China
// target while editing genuinely on `.us`) is `unsupportedRegion` instead — checked before
// the owner comparison, so the site-specific "unreachable" message wins over the generic
// "doesn't match this record" one.
export function validateEditTarget(
    items: TenantScoped[],
    notificationTenantId: TenantId | undefined
): TenantValidationResult {
    const analysis = analyzeTargetTenants(items);
    if (analysis.status !== "valid") {
        return analysis;
    }
    if (!isTenantReachableFromCurrentSite(analysis.tenantId)) {
        return { status: "unsupportedRegion", tenantId: analysis.tenantId };
    }
    if (notificationTenantId === undefined) {
        return { status: "unresolved" };
    }
    if (analysis.tenantId !== notificationTenantId) {
        return { status: "mismatch", tenantId: analysis.tenantId, writeTenantId: notificationTenantId };
    }
    return analysis;
}

// Matches NotificationService's own serviceRoute.prefix — must be a path
// getRequestBaseUrl() recognizes for the US/China/Russia gateway fallback logic below.
const NOTIFICATION_SERVICE_PATH = "notification/v1/notifications";

const TENANT_ID_TO_GATEWAY_RCC: { [key: string]: RccCode } = {
    "1": RccCodes.US,
    "2": RccCodes.China,
    "3": RccCodes.Russia,
};

// True only if a tenant's gateway is actually configured for this build. gl-commonui
// silently falls back to the Global gateway whenever a tenant-specific apiPrefixV2* prefix
// is unset, which would otherwise let a forced write silently land on Global while the UI
// claims it went to the intended tenant.
//
// The US notification deployment (genuine `.us`, or `deploymentRcc: "us"` on a `.com`
// build — US School TEST/PROD run at a `.com` hostname) is a deliberate exception: it sets
// `apiPrefixV2` itself to the US V2 gateway (nexus/student/connect already route through it
// there) and never sets a separate `apiPrefixV2US` — gl-commonui's own `getRequestBaseUrl()`
// already falls back to `apiPrefixV2` for a US-context notification request when
// `apiPrefixV2US` is unset, so the resolved URL IS correct, it just can no longer be told
// apart from the forced-Global URL by the usual "must differ" check (both resolve to the
// same `apiPrefixV2`). Accepting any non-empty resolved URL here is safe specifically
// because `isTenantReachableFromCurrentSite()` is what actually keeps this same deployment
// from ever reaching Global/China/Russia — this relaxation only ever applies to the one
// tenant it's already restricted to.
export function isTenantGatewayConfigured(tenantId: TenantId): boolean {
    if (tenantId === null) {
        return true;
    }
    const gatewayRcc = TENANT_ID_TO_GATEWAY_RCC[String(tenantId)];
    if (gatewayRcc === undefined) {
        return false;
    }
    const scopedUrl = getRequestBaseUrl(gatewayRcc, NOTIFICATION_SERVICE_PATH, "");
    if (tenantId === 1 && isUSNotificationDeployment()) {
        return !!scopedUrl;
    }
    const globalUrl = getRequestBaseUrl(gatewayRcc, NOTIFICATION_SERVICE_PATH, "", true);
    return scopedUrl !== globalUrl;
}

// The single source of truth for which gateway a CREATE write is forced to.
//
// `.ru` has its own fully split admin/account DB, so a genuine Russia user can never
// resolve a target to a different tenant — the write force-routes straight to Russia's own
// gateway there, independent of what the (unreliable, split-DB) target analysis says, still
// failing closed if Russia's own gateway isn't configured for this build.
//
// Everywhere else, the gateway is derived from the resolved target: fails closed
// (undefined) for mixed/unresolved/empty/unmapped/unreachable targets, or a known tenant
// with no gateway configured for this build. `NotificationService.create()` must refuse to
// write at all when this returns undefined.
export function resolveWriteRcc(items: TenantScoped[]): RccCode | undefined {
    if (isCurrentSiteRussia()) {
        const rcc = isTenantGatewayConfigured(3) ? tenantIdToWriteRcc(3) : undefined;
        logNotificationTenantDebug("resolveWriteRcc", { site: "ru", resolvedRcc: rcc });
        return rcc;
    }
    const analysis = analyzeCreateTarget(items);
    if (analysis.status !== "valid" || !isTenantGatewayConfigured(analysis.tenantId)) {
        logNotificationTenantDebug("resolveWriteRcc", { site: "other", status: analysis.status, resolvedRcc: undefined });
        return undefined;
    }
    const rcc = tenantIdToWriteRcc(analysis.tenantId);
    logNotificationTenantDebug("resolveWriteRcc", { site: "other", status: analysis.status, resolvedRcc: rcc });
    return rcc;
}

// Explicit owner-tenant routing mode for every tenant-scoped NotificationService call
// (manageNotifications/getDetail/update/delete/statistics — never create, which always
// force-routes from the resolved target via resolveWriteRcc()):
// - `"ambient"` — no explicit region context; ride whatever gateway gl-commonui's own
//   current-site/token resolution already sends the request to.
// - `"selectedRegion"` — an explicit tenant; force-routes via `__gl_region__`
//   (resolveOwnerRcc()).
export type NotificationRoutingContext =
    | { mode: "ambient" }
    | { mode: "selectedRegion"; tenantId: TenantId };

// The single source of truth every tenant-scoped call (list/detail/update/delete/
// statistics) uses to force its own request to an explicit owner tenant. Fails closed for
// an unresolved owner, a tenant unreachable from the current site (a non-Russia owner on
// `.ru`, or Russia anywhere else), or one whose gateway isn't configured for this build.
export function resolveOwnerRcc(ownerTenantId: TenantId | undefined): RccCode | undefined {
    if (
        ownerTenantId === undefined ||
        !isTenantReachableFromCurrentSite(ownerTenantId) ||
        !isTenantGatewayConfigured(ownerTenantId)
    ) {
        return undefined;
    }
    return tenantIdToWriteRcc(ownerTenantId);
}

// Appends gl-commonui's __gl_region__ override, which buildRequestUrl() reads to pick the
// write gateway and strips before the request leaves. The only mechanism that forces a
// notification/* request to a specific tenant's gateway — every other admin/account call
// keeps resolving through its own unrelated, unchanged routing.
export function appendRegionOverride(url: string, rcc: RccCode): string {
    const separator = url.indexOf("?") === -1 ? "?" : "&";
    return `${url}${separator}__gl_region__=${encodeURIComponent(rcc)}`;
}

// Optional, opt-in debug aid: entirely inactive unless the current page URL carries
// `?debugNotificationTenant=1`. Lives here (not in page-level logic) so both `service.ts`
// and page code can call it without a service ever importing page logic.
export function isNotificationTenantDebugEnabled(): boolean {
    return typeof window !== "undefined" && !!window.location &&
        /[?&]debugNotificationTenant=1(?:&|$)/.test(window.location.search || "");
}

// Logs only non-sensitive routing/validation metadata explicitly passed in `data` (site
// classification, validation status, role/targetType, resolved rcc, mapped scopeType,
// source totals, dedupe count, creationDate order) — never a token, user data, a
// notification's own title/description, or a full request/response payload. Sanitization
// discipline is entirely at each call site.
export function logNotificationTenantDebug(label: string, data: { [key: string]: any }): void {
    if (!isNotificationTenantDebugEnabled()) {
        return;
    }
    // tslint:disable-next-line:no-console
    console.log(`[notification-tenant-debug] ${label}`, data);
}
